import type { ReactNode } from 'react';

import {
  BpkBox,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import classNames from 'classnames';

import DesktopScrollContainer from '../common/DesktopScrollContainer/DesktopScrollContainer';
import FooterBlurb from '../common/FooterBlurb/index';

import STYLES from './CardRail.module.scss';

export type CardRailLayout = 'desktop' | 'mobile';

type CardRailProps = {
  title?: string;
  subtitle?: string;
  children?: JSX.Element[] | ((layout: CardRailLayout) => JSX.Element[]);
  callToAction?: ReactNode;
  bottomNav?: boolean;
  onClickPageIndicator?: () => void;
  mobileScrollerRef?: (el: HTMLElement | null) => void;
  disclaimer?: string;
  chips?: ReactNode;
  scrollableOnTablet?: boolean;
};

const CardRail = ({
  bottomNav = false,
  callToAction = null,
  children = [],
  chips = null,
  disclaimer = undefined,
  mobileScrollerRef = undefined,
  onClickPageIndicator = undefined,
  scrollableOnTablet = false,
  subtitle = undefined,
  title = undefined,
}: CardRailProps) => {
  const renderChildren = (layout: CardRailLayout) =>
    typeof children === 'function' ? children(layout) : children;

  return (
    <BpkVessel
      className={classNames(STYLES.CardRail, {
        [STYLES.ScrollableOnTablet]: scrollableOnTablet,
      })}
    >
      {title && (
        <BpkVessel className={STYLES.header}>
          <BpkSectionHeader title={title} description={subtitle} />
        </BpkVessel>
      )}
      {/*
        We first tried using BpkBreakpoint to switch between BpkMobileScrollContainer and DesktopScrollContainer
        But find it not possible for the server-side rendering page of hotels-in-city,
        because the server doesn't have the window or concept of what to apply until it hits the user device.
        So we move to using style breakpoint to switch between the components.
        Refer to the thread:
        https://skyscanner.slack.com/archives/C0305N26PQC/p1702472300782129?thread_ts=1702383597.603779&cid=C0305N26PQC
      */}
      <BpkVessel className={STYLES.mobile_scroll_container_hider}>
        <BpkMobileScrollContainer scrollerRef={mobileScrollerRef}>
          <BpkFlex
            gap={BpkSpacing.Base}
            padding={BpkSpacing.Base}
            data-testid="CardRailMobileScrollContainerBody"
          >
            {renderChildren('mobile')}
          </BpkFlex>
        </BpkMobileScrollContainer>
      </BpkVessel>
      <DesktopScrollContainer
        bottomNav={bottomNav}
        nextLabel="next"
        prevLabel="prev"
        onClickPageIndicator={onClickPageIndicator}
      >
        {renderChildren('desktop')}
      </DesktopScrollContainer>
      {callToAction}
      {disclaimer && (
        <BpkBox>
          <FooterBlurb disclaimerCopy={disclaimer} />
        </BpkBox>
      )}
    </BpkVessel>
  );
};

export default CardRail;
